import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  fetchPlayerOverview,
  fetchPlayerPassbook,
} from '../Operations/PlayerDetails/services/playerDetailsService';

import type {
  PassbookItem,
  PassbookPagination,
  PlayerOverview,
} from '../Operations/PlayerDetails/types';

type LedgerFilter =
  | 'all'
  | 'bet_placed'
  | 'bet_won'
  | 'bet_lost'
  | 'deposit'
  | 'withdrawal_completed';

const toNumberOrNull = (
  value: string | number | null | undefined
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

const formatMoney = (value?: number | null) =>
  new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value?: string) => {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getSelectedNumber = (item: PassbookItem) => {
  const firstBet = item.bet_details?.placed_bets?.[0];

  return (
    firstBet?.jodi ??
    firstBet?.combination ??
    firstBet?.pana ??
    firstBet?.digit ??
    item.bet_details?.jodi ??
    item.bet_details?.close_pana ??
    item.bet_details?.close_digit ??
    item.bet_details?.open_pana ??
    item.bet_details?.open_digit ??
    null
  );
};

const getTotalBefore = (item: PassbookItem): number | null => {
  const metadata = item.ledger_metadata;

  const explicitTotal = toNumberOrNull(
    metadata?.player_balance_before
  );

  if (explicitTotal !== null) {
    return explicitTotal;
  }

  const available = toNumberOrNull(
    metadata?.available_before
  );

  const locked = toNumberOrNull(
    metadata?.locked_before
  );

  if (available !== null && locked !== null) {
    return available + locked;
  }

  return null;
};

const getTotalAfter = (item: PassbookItem): number | null => {
  const metadata = item.ledger_metadata;

  const explicitTotal = toNumberOrNull(
    metadata?.player_balance_after
  );

  if (explicitTotal !== null) {
    return explicitTotal;
  }

  const available = toNumberOrNull(
    metadata?.available_after
  );

  const locked = toNumberOrNull(
    metadata?.locked_after
  );

  if (available !== null && locked !== null) {
    return available + locked;
  }

  return null;
};

const getNetChange = (item: PassbookItem): number | null => {
  const metadata = item.ledger_metadata;

  const explicitChange = toNumberOrNull(
    metadata?.player_net_change
  );

  if (explicitChange !== null) {
    return explicitChange;
  }

  const before = getTotalBefore(item);
  const after = getTotalAfter(item);

  if (before !== null && after !== null) {
    return after - before;
  }

  return null;
};

const buildRemark = (item: PassbookItem) => {
  const market = item.market?.name;
  const betType = item.bet_type?.name;
  const session = item.bet_details?.session;
  const selectedNumber = getSelectedNumber(item);

  const transactionType =
    item.transaction_type || item.type || '';

  const betAmount = Math.abs(
    Number(item.bet_amount || item.amount || 0)
  );

  const winAmount = Math.abs(
    Number(item.win_amount || item.amount || 0)
  );

  if (
    ['bet_placed', 'bet_lost', 'bet_won'].includes(
      transactionType
    ) &&
    market &&
    betType
  ) {
    let amountForRemark = betAmount;

    if (transactionType === 'bet_won' && winAmount > 0) {
      amountForRemark = winAmount;
    }

    const gameParts = [
      market.toUpperCase(),
      betType.toUpperCase(),
      session ? session.toUpperCase() : null,
    ].filter(Boolean);

    return `₹${formatMoney(
      amountForRemark
    )} - ${gameParts.join(' - ')}${
      selectedNumber !== null
        ? ` : ${selectedNumber}`
        : ''
    }`;
  }

  return (
    item.description ||
    item.type_display ||
    item.transaction_type ||
    'Transaction'
  );
};

const WalletLedgerPreview: React.FC = () => {
  const params = useParams<{ playerId?: string }>();

  const playerId = params.playerId || '15';

  const [player, setPlayer] =
    useState<PlayerOverview | null>(null);

  const [rows, setRows] = useState<PassbookItem[]>([]);

  const [pagination, setPagination] =
    useState<PassbookPagination | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [type, setType] =
    useState<LedgerFilter>('all');

  const [fromDateInput, setFromDateInput] =
    useState('');

  const [toDateInput, setToDateInput] =
    useState('');

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [searchInput, setSearchInput] =
    useState('');

  const [search, setSearch] = useState('');

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [overview, ledger] = await Promise.all([
        fetchPlayerOverview(playerId),
        fetchPlayerPassbook(
          playerId,
          page,
          pageSize,
          type,
          search,
          fromDate,
          toDate
        ),
      ]);

      setPlayer(overview);
      setRows(ledger.data || []);
      setPagination(ledger.pagination || null);
    } catch (err: any) {
      setPlayer(null);
      setRows([]);
      setPagination(null);

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to load Wallet Ledger preview.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    playerId,
    page,
    pageSize,
    type,
    search,
    fromDate,
    toDate,
  ]);

  const currentPage =
    pagination?.current_page || page;

  const totalPages =
    pagination?.total_pages || 1;

  const totalCount =
    pagination?.total_count || rows.length;

  const startRow =
    totalCount === 0
      ? 0
      : (currentPage - 1) * pageSize + 1;

  const endRow = Math.min(
    currentPage * pageSize,
    totalCount
  );

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
    setFromDate(fromDateInput);
    setToDate(toDateInput);
  };

  const handleReset = () => {
    setType('all');

    setFromDateInput('');
    setToDateInput('');

    setFromDate('');
    setToDate('');

    setSearchInput('');
    setSearch('');

    setPage(1);
  };

  const handleExport = () => {
    const header =
      'Sr.No,Total Before,Total Change,Total After,Remark,Created\n';

    const body = rows
      .map((item, index) => {
        const serial =
          (currentPage - 1) * pageSize +
          index +
          1;

        return [
          serial,
          getTotalBefore(item) ?? '',
          getNetChange(item) ?? '',
          getTotalAfter(item) ?? '',
          `"${buildRemark(item).replace(
            /"/g,
            '""'
          )}"`,
          `"${formatDateTime(
            item.created_at
          )}"`,
        ].join(',');
      })
      .join('\n');

    const blob = new Blob(
      [header + body],
      {
        type: 'text/csv;charset=utf-8;',
      }
    );

    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = url;

    link.download =
      `player-${playerId}-wallet-ledger-preview-page-${currentPage}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const renderPageButtons = () => {
    const pages: number[] = [];

    const start = Math.max(
      1,
      currentPage - 2
    );

    const end = Math.min(
      totalPages,
      currentPage + 2
    );

    for (
      let pageNumber = start;
      pageNumber <= end;
      pageNumber += 1
    ) {
      pages.push(pageNumber);
    }

    return pages;
  };

  const previewTableStyle = `
    .wallet-ledger-preview-table {
      border-color: #e6e9ed;
    }

    .wallet-ledger-preview-table {
      border-collapse: collapse !important;
      width: 100%;
    }

    .wallet-ledger-preview-table th {
      background: #f8f9fb !important;
      color: #707987;
      font-size: 11px;
      font-weight: 600;
      padding: 7px 8px !important;
      vertical-align: middle;
      border: 1px solid #dfe3e8 !important;
    }

    .wallet-ledger-preview-table td {
      padding: 5px 8px !important;
      vertical-align: middle;
      border: 1px solid #e3e6ea !important;
    }

    .wallet-ledger-preview-table tbody tr {
      border-bottom: 1px solid #e3e6ea !important;
    }

    .wallet-ledger-preview-table tbody tr:hover {
      background: #fafbfd;
    }

    .wallet-ledger-preview-table input[type="checkbox"] {
      width: 13px;
      height: 13px;
    }
  `;

  return (
    <div className="container-fluid py-3">
      <style>{previewTableStyle}</style>
      <div className="card border-0 shadow-sm">
        <div
          className="d-flex align-items-center justify-content-between px-3 text-white"
          style={{
            backgroundColor: '#1197d5',
            minHeight: 48,
          }}
        >
          <h5
            className="mb-0 text-white fw-semibold"
            style={{ fontSize: 15 }}
          >
            Summary
          </h5>

          <span
            aria-hidden="true"
            style={{
              fontSize: 22,
              lineHeight: 1,
              opacity: 0.9,
            }}
          >
            ×
          </span>
        </div>

        <div className="card-body border-bottom">
          <div className="row g-3 align-items-end">
            <div className="col-xl-3 col-lg-4 col-md-6">
              <label className="form-label fw-semibold">
                Select Type
              </label>

              <select
                className="form-select"
                value={type}
                onChange={(event) => {
                  setType(
                    event.target.value as LedgerFilter
                  );

                  setPage(1);
                }}
              >
                <option value="all">All</option>

                <option value="bet_placed">
                  Bet Placed
                </option>

                <option value="bet_won">
                  Bet Won
                </option>

                <option value="bet_lost">
                  Bet Lost
                </option>

                <option value="deposit">
                  Deposit
                </option>

                <option value="withdrawal_completed">
                  Withdrawal
                </option>
              </select>
            </div>

            <div className="col-xl-3 col-lg-4 col-md-6">
              <label className="form-label fw-semibold">
                Select From Date
              </label>

              <input
                type="date"
                className="form-control"
                value={fromDateInput}
                onChange={(event) =>
                  setFromDateInput(
                    event.target.value
                  )
                }
              />
            </div>

            <div className="col-xl-3 col-lg-4 col-md-6">
              <label className="form-label fw-semibold">
                Select To Date
              </label>

              <input
                type="date"
                className="form-control"
                value={toDateInput}
                onChange={(event) =>
                  setToDateInput(
                    event.target.value
                  )
                }
              />
            </div>

            <div className="col-xl-3 col-lg-12 col-md-6">
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-success px-4"
                  onClick={handleSearch}
                >
                  Search
                </button>

                <button
                  type="button"
                  className="btn btn-light border px-3"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body">
          {error && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div>
              <h5 className="mb-1">
                User Summary List -{' '}
                {player
                  ? `${
                      player.full_name ||
                      `${player.first_name} ${player.last_name}`.trim()
                    } (${player.mobile_number})`
                  : `Player #${playerId}`}
              </h5>

            </div>

            <button
              type="button"
              className="btn btn-info text-white px-4"
              disabled={
                loading || rows.length === 0
              }
              onClick={handleExport}
            >
              Export
            </button>
          </div>

          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm"
                  style={{ width: 68, fontSize: 12 }}
                value={pageSize}
                onChange={(event) => {
                  setPageSize(
                    Number(event.target.value)
                  );

                  setPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>

              <span className="small">
                entries per page
              </span>
            </div>

            <div className="d-flex align-items-center gap-2">
              <label className="small mb-0">
                Search:
              </label>

              <input
                type="search"
                className="form-control form-control-sm"
                  style={{ width: 190, fontSize: 12 }}
                value={searchInput}
                onChange={(event) =>
                  setSearchInput(
                    event.target.value
                  )
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>
          </div>

            <div className="table-responsive px-1">
              <table className="table table-sm table-bordered table-hover align-middle mb-0 wallet-ledger-preview-table">
              <thead className="table-light">
                <tr>
                    <th className="text-center" style={{ width: 34 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all"
                    />
                  </th>

                    <th className="text-center" style={{ width: 58 }}>
                    Sr.No
                  </th>

                    <th className="text-center text-nowrap" style={{ width: 105 }}>
                    Total Before
                  </th>

                    <th className="text-center text-nowrap" style={{ width: 90 }}>
                    Total Change
                  </th>

                    <th className="text-center text-nowrap" style={{ width: 105 }}>
                    Total After
                  </th>

                    <th className="text-start" style={{ minWidth: 360 }}>
                    Remark
                  </th>

                    <th className="text-center text-nowrap" style={{ width: 170 }}>
                    Created
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-4 text-muted"
                    >
                      Loading real player ledger...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-4 text-muted"
                    >
                      No ledger transactions found.
                    </td>
                  </tr>
                ) : (
                  rows.map((item, index) => {
                    const serial =
                      (currentPage - 1) * pageSize +
                      index +
                      1;

                    const before =
                      getTotalBefore(item);

                    const after =
                      getTotalAfter(item);

                    const change =
                      getNetChange(item);

                    return (
                        <tr
                          key={item.id}
                          style={{ fontSize: 11, height: 30 }}
                        >
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Select row ${serial}`}
                          />
                        </td>

                          <td className="text-center py-1">{serial}</td>

                          <td className="text-center py-1">
                          {before === null
                            ? '—'
                            : formatMoney(before)}
                        </td>

                        <td
                            className={`text-center py-1 fw-semibold ${
                            change !== null && change > 0
                              ? 'text-success'
                              : change !== null && change < 0
                                ? 'text-danger'
                                : 'text-muted'
                          }`}
                        >
                          {change !== null && change > 0 ? '+' : ''}
                          {change === null ? '—' : formatMoney(change)}
                        </td>

                          <td className="text-center py-1">
                          {after === null
                            ? '—'
                            : formatMoney(after)}
                        </td>

                          <td className="py-1 text-nowrap">
                          {buildRemark(item)}
                        </td>

                        <td className="text-nowrap">
                          {formatDateTime(
                            item.created_at
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

            </table>
          </div>

          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
            <div className="small text-muted">
              Showing {startRow} to {endRow} of{' '}
              {totalCount} entries
            </div>

            <div className="btn-group">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={
                  loading ||
                  currentPage <= 1
                }
                onClick={() =>
                  setPage((value) =>
                    Math.max(1, value - 1)
                  )
                }
              >
                Previous
              </button>

              {renderPageButtons().map(
                (pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={`btn btn-sm ${
                      currentPage ===
                      pageNumber
                        ? 'btn-primary'
                        : 'btn-outline-secondary'
                    }`}
                    onClick={() =>
                      setPage(pageNumber)
                    }
                  >
                    {pageNumber}
                  </button>
                )
              )}

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={
                  loading ||
                  currentPage >= totalPages
                }
                onClick={() =>
                  setPage((value) =>
                    Math.min(
                      totalPages,
                      value + 1
                    )
                  )
                }
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletLedgerPreview;
